import { Button } from '@/design-system/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/design-system/select';
import { useDynamicLocale } from '@/hoslog-intl';
import { exportToCSV } from '@/lib/exportToCSV';
import { useState } from 'react';
import { useSalesBreakdown } from '../hooks/useOverview';
import { SalesBreakdownTable } from './table/sales-breakdown';

const SALES_BREAKDOWN_START_YEAR = 2026;

export const SalesBreakdown = () => {
  const { t } = useDynamicLocale();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(() => String(new Date(currentYear, 0, 1).getTime()));
  const { salesBreakdownData, pagination, isLoading } = useSalesBreakdown({ year: Number(selectedYear) });

  const yearOptions = Array.from({ length: Math.max(currentYear - SALES_BREAKDOWN_START_YEAR + 1, 0) }, (_, index) => {
    const year = SALES_BREAKDOWN_START_YEAR + index;
    return {
      value: String(new Date(year, 0, 1).getTime()),
      label: t('paymentManagement.overview.salesBreakdown.yearOption', { year }),
    };
  });

  const onExport = () => {
    const exportRows = salesBreakdownData.map((item, index) => ({
      [t('common.page.tableColumn.sn')]: index + 1,
      [t('common.page.tableColumn.month')]: item.month || `${item.month}月`,
      [t('common.page.tableColumn.numberOfCustomers')]: item.numberOfCustomer,
      [t('common.page.tableColumn.numberOfStores')]: item.numberOfStore,
      [t('common.page.tableColumn.numberOfAffiliates')]: item.numberOfAffiliate,
      [t('common.page.tableColumn.receivedAmount')]: item.receiveAmount,
    }));

    exportToCSV(exportRows, {
      filename: `sales-breakdown`,
      excludeColumns: ['select', 'actions'],
      onlySelected: false,
    });
  };

  const onExportRow = (item: (typeof salesBreakdownData)[number], serialNumber: number) => {
    const monthLabel = item.monthName || (item.month ? `${item.month}月` : '');
    const exportRow = {
      [t('common.page.tableColumn.sn')]: serialNumber,
      [t('common.page.tableColumn.month')]: monthLabel,
      [t('common.page.tableColumn.numberOfCustomers')]: item.numberOfCustomer,
      [t('common.page.tableColumn.numberOfStores')]: item.numberOfStore,
      [t('common.page.tableColumn.numberOfAffiliates')]: item.numberOfAffiliate,
      [t('common.page.tableColumn.receivedAmount')]: item.receiveAmount,
    };

    exportToCSV([exportRow], {
      filename: monthLabel ? `sales-breakdown-${monthLabel}` : 'sales-breakdown',
      excludeColumns: ['select', 'actions'],
      onlySelected: false,
    });
  };

  return (
    <div>
      <div className=" font-bold">{t('paymentManagement.overview.tabs.salesBreakdown')}</div>
      <div className="flex gap-3 mt-4 mb-2.5">
        <Select value={selectedYear} onValueChange={setSelectedYear}>
          <SelectTrigger id="sort-select" className="bg-card border-none w-[138px]">
            <SelectValue placeholder={t('paymentManagement.overview.salesBreakdown.selectYear')} />
          </SelectTrigger>
          <SelectContent>
            {yearOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button onClick={onExport}>{t('common.page.table.exportCSVText')}</Button>
      </div>

      <SalesBreakdownTable
        data={salesBreakdownData}
        pagination={pagination}
        loading={isLoading}
        onExportRow={onExportRow}
      />
    </div>
  );
};
